import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Loader, Typography } from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { SANITIZED_DATE_FORMATS_MAPPING } from '../../../../../../utils/constant';
import {
  getTranslatedMessageUsingId,
  useInvertedLocalize,
} from '../../../../../../utils/localization';
import { getMessageKey } from '../../../../../../utils/utils';
import { invertedMessages } from '../../../messages';
import { FieldSet, SpbTimeLine } from '../../../SidePanelCommon';
import { TAB_HISTORY } from '../constant';
import DATA from '../mockData';
import { textWhenNoDataTextForSidePanel } from '../utils';

function CertsSidePanelBodyHistory({ data, isLoadingHistory, isMobileWidth }) {
  const intl = useIntl();
  const { globalDateFormat } = useSelector(
    (state) => state?.certifications?.details?.global
  );
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);

  const reassignedLabel =
    messages['reassigned.ATTESTATIONS.label'] ||
    localize(intl, getMessageKey('reassigned.ATTESTATIONS.label'));
  const reassignedByLabel =
    messages.CERTS_REASSIGNED_BY ||
    localize(intl, getMessageKey('CERTS_REASSIGNED_BY'));
  const reassignedOnLabel =
    messages.CERTS_REASSIGNED_ON ||
    localize(intl, getMessageKey('CERTS_REASSIGNED_ON'));
  const commentsLabel =
    messages.CERTS_COMMENTS || localize(intl, getMessageKey('CERTS_COMMENTS'));

  const consultedLabel =
    messages.CERTS_CONSULTED ||
    localize(intl, getMessageKey('CERTS_CONSULTED'));
  const consultedOnLabel =
    messages.CERTS_CONSULTED_ON ||
    localize(intl, getMessageKey('CERTS_CONSULTED_ON')); // CERTS_CONSULTED_ON
  const consultedByLabel =
    messages.CERTS_CONSULTED_BY ||
    localize(intl, getMessageKey('CERTS_CONSULTED_BY')); // CERTS_CONSULTED_BY

  const certifiedLabel =
    messages.CERTS_CERTIFIED ||
    localize(intl, getMessageKey('CERTS_CERTIFIED'));
  const certifiedOnLabel =
    messages.CERTS_CERTIFIED_ON ||
    localize(intl, getMessageKey('CERTS_CERTIFIED_ON'));
  const certifiedByLabel =
    messages.CERTS_CERTIFIED_BY ||
    localize(intl, getMessageKey('CERTS_CERTIFIED_BY'));

  const approvedLabel =
    messages.CERTS_APPROVED || localize(intl, getMessageKey('CERTS_APPROVED'));
  const approvedOnLabel =
    messages.CERTS_APPROVED_ON ||
    localize(intl, getMessageKey('CERTS_APPROVED_ON'));
  const approvedByLabel =
    messages.CERTS_APPROVED_BY ||
    localize(intl, getMessageKey('CERTS_APPROVED_BY'));

  const rejectedLabel =
    messages.CERTS_REJECTED || localize(intl, getMessageKey('CERTS_REJECTED'));
  const rejectedOnLabel =
    messages.CERTS_REJECTED_ON ||
    localize(intl, getMessageKey('CERTS_REJECTED_ON'));
  const rejectedByLabel =
    messages.CERTS_REJECTED_BY ||
    localize(intl, getMessageKey('CERTS_REJECTED_BY'));

  const worksForMeLabel =
    messages.CERTS_WORKS_FOR_ME ||
    localize(intl, getMessageKey('CERTS_WORKS_FOR_ME'));

  const resetLabel =
    messages.CERTS_RESET || localize(intl, getMessageKey('CERTS_RESET'));

  const auditHistoryActionType = {
    reassigned: {
      icon: 'ReassignUser',
      iconColor: 'neutral-000',
      heading: reassignedLabel,
      date: reassignedOnLabel,
      user: reassignedByLabel,
      other: commentsLabel,
    },
    doesNotReport: {
      icon: 'ReassignItem',
      iconColor: 'neutral-000',
      heading: reassignedLabel,
      date: reassignedOnLabel,
      user: reassignedByLabel,
      other: commentsLabel,
    },
    consulted: {
      icon: 'Consult',
      iconColor: 'neutral-000',
      heading: consultedLabel,
      date: consultedOnLabel,
      user: consultedByLabel,
      other: commentsLabel,
    },
    ChangeEnddate: {
      icon: 'ExtendEndDate',
      iconColor: 'navy-700',
      heading: certifiedLabel,
      date: certifiedOnLabel,
      user: certifiedByLabel,
      other: commentsLabel,
    },
    conditionallyCertified: {
      icon: 'ExtendEndDate',
      iconColor: 'success-700',
      heading: certifiedLabel,
      date: certifiedOnLabel,
      user: certifiedByLabel,
      other: commentsLabel,
    },
    Reject: {
      icon: 'Reject',
      iconColor: 'critical-700',
      heading: rejectedLabel,
      date: rejectedOnLabel,
      user: rejectedByLabel,
      other: commentsLabel,
    },
    Decommissioned: {
      icon: 'Reject',
      iconColor: 'critical-700',
      heading: rejectedLabel,
      date: rejectedOnLabel,
      user: rejectedByLabel,
      other: commentsLabel,
    },
    terminated: {
      icon: 'Terminate',
      iconColor: 'critical-700',
      heading: approvedLabel,
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
    worksForMe: {
      icon: 'ConfirmManager',
      iconColor: 'success-700',
      heading: approvedLabel,
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
    belongsToMe: {
      icon: 'Check',
      iconColor: 'success-700',
      heading: approvedLabel,
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
    Approve: {
      icon: 'Check',
      iconColor: 'success-700',
      heading: worksForMeLabel,
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
    SaviCompanionSmartApprove: {
      icon: 'Check',
      iconColor: 'purple-900',
      heading: approvedLabel,
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
    SaviCompanionSmartReject: {
      icon: 'Reject',
      iconColor: 'purple-900',
      heading: worksForMeLabel,
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
    cancelled: {
      icon: 'Close',
      iconColor: 'navy-700',
      heading: resetLabel,
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
    default: {
      icon: 'Close',
      iconColor: 'neutral-000',
      heading: 'NOT_PROVIDED',
      date: approvedOnLabel,
      user: approvedByLabel,
      other: commentsLabel,
    },
  };

  return (
    <>
      {isLoadingHistory && (
        <Box className='SidePanelView-loader'>
          <Loader kind='dots' format='inline' color='info' size='medium' />
        </Box>
      )}
      {!isLoadingHistory && data?.sidePanel?.history?.audit?.length === 0 && (
        <Typography
          kind='body2'
          className='CertsSidePanelBody-tabContentTimeLineBlock--noData'>
          {l(textWhenNoDataTextForSidePanel(TAB_HISTORY))}
        </Typography>
      )}
      {!isLoadingHistory && data?.sidePanel?.history?.audit?.length > 0 && (
        <>
          <Typography
            kind='body3'
            className={
              isMobileWidth
                ? 'CertsSidePanelBody-tabContentDescription--mobileResponsive'
                : 'CertsSidePanelBody-tabContentDescription'
            }>
            {messages.CERTS_LIST_REVIEW_ACTIONS_MSG ||
              localize(intl, getMessageKey('CERTS_LIST_REVIEW_ACTIONS_MSG'))}
          </Typography>
          {data?.sidePanel?.history?.audit?.map((item, index) => {
            const actionDate = moment
              .utc(
                item.actionDate,
                SANITIZED_DATE_FORMATS_MAPPING?.[item.actionDateFormat]
              )
              .local()
              .format(`${globalDateFormat?.toUpperCase()} hh:mm:ss`);

            const checkIconType =
              item.action?.toLowerCase()?.includes('cancelled') ||
              item.action?.toLowerCase()?.includes('consult') ||
              item.action?.toLowerCase()?.includes('reassign') ||
              item.action?.toLowerCase()?.includes('does not') ||
              item.action?.trim()?.length === 0
                ? 'fill'
                : 'outline';

            let checkIconKind;

            switch (true) {
              case item.action?.toLowerCase()?.includes('cancelled'):
              case item.action?.toLowerCase()?.includes('consult'):
              case item.action?.toLowerCase()?.includes('reassign'):
              case item.action?.toLowerCase()?.includes('certified'):
              case item.action?.toLowerCase()?.includes('does not'):
              case item.action?.toLowerCase() === 'change enddate' ||
                item.action?.toLowerCase() === 'changed enddate':
              case item.action?.trim()?.length === 0:
                checkIconKind = 'normal';
                break;
              case item.action?.toLowerCase()?.includes('terminated'):
              case item.action?.toLowerCase()?.includes('reject'):
              case item.action?.toLowerCase()?.includes('decommissioned'):
                checkIconKind = 'critical';
                break;
              case item.action?.toLowerCase()?.includes('savi'):
                checkIconKind = 'savi';
                break;
              default:
                checkIconKind = 'success';
                break;
            }

            let checkIconShape;

            switch (true) {
              case item.action?.toLowerCase()?.includes('approve') &&
                !item.action?.toLowerCase()?.includes('savi'):
                checkIconShape = auditHistoryActionType?.Approve?.icon;
                break;
              case item.action?.toLowerCase()?.includes('belongs') &&
                !item.action?.toLowerCase()?.includes('savi'):
                checkIconShape = auditHistoryActionType?.belongsToMe?.icon;
                break;
              case item.action?.toLowerCase()?.includes('confirm manager') &&
                !item.action?.toLowerCase()?.includes('date'):
                checkIconShape = auditHistoryActionType?.worksForMe?.icon;
                break;
              case item.action?.toLowerCase()?.includes('extendenddate') ||
                (item.action?.toLowerCase()?.includes('confirm manager') &&
                  item.action?.toLowerCase()?.includes('date')):
                checkIconShape =
                  auditHistoryActionType?.conditionallyCertified?.icon;
                break;
              case item.action?.toLowerCase()?.includes('date'):
                checkIconShape =
                  auditHistoryActionType?.conditionallyCertified?.icon;
                break;
              case item.action?.toLowerCase()?.includes('does not'):
                checkIconShape = auditHistoryActionType?.doesNotReport?.icon;
                break;
              case item.action?.toLowerCase()?.includes('reassign'):
                checkIconShape = auditHistoryActionType?.reassigned?.icon;
                break;
              case item.action?.trim()?.length === 0:
                checkIconShape = auditHistoryActionType?.default?.icon;
                break;
              default:
                checkIconShape =
                  auditHistoryActionType[item.action?.replaceAll(' ', '')]
                    ?.icon || auditHistoryActionType?.default?.icon;
                break;
            }

            let checkIconColor;

            switch (true) {
              case item.action?.toLowerCase()?.includes('approve') &&
                !item.action?.toLowerCase()?.includes('savi'):
                checkIconColor = auditHistoryActionType?.Approve?.iconColor;
                break;
              case item.action?.toLowerCase()?.includes('belongs') &&
                !item.action?.toLowerCase()?.includes('savi'):
                checkIconColor = auditHistoryActionType?.belongsToMe?.iconColor;
                break;
              case item.action?.toLowerCase()?.includes('confirm manager') &&
                !item.action?.toLowerCase()?.includes('date'):
                checkIconColor = auditHistoryActionType?.worksForMe?.iconColor;
                break;
              case item.action?.toLowerCase()?.includes('extendenddate') ||
                (item.action?.toLowerCase()?.includes('confirm manager') &&
                  item.action?.toLowerCase()?.includes('date')):
                checkIconColor =
                  auditHistoryActionType?.conditionallyCertified?.iconColor;
                break;
              case item.action?.toLowerCase()?.includes('does not'):
                checkIconColor =
                  auditHistoryActionType?.doesNotReport?.iconColor;
                break;
              case item.action?.toLowerCase()?.includes('reassign'):
                checkIconColor = auditHistoryActionType?.reassigned?.iconColor;
                break;
              case item.action?.toLowerCase()?.includes('change enddate ') ||
                item.action?.toLowerCase()?.includes('changed enddate '):
                checkIconColor =
                  auditHistoryActionType?.conditionallyCertified?.iconColor;
                break;
              case item.action?.trim()?.length === 0:
                checkIconColor = auditHistoryActionType?.default?.iconColor;
                break;
              default:
                checkIconColor =
                  auditHistoryActionType[item.action?.replaceAll(' ', '')]
                    ?.iconColor || auditHistoryActionType?.default?.iconColor;
                break;
            }

            return (
              <div
                key={item.index}
                className='CertsSidePanelBody-tabContentTimeLineBlock'>
                <SpbTimeLine
                  iconKind={checkIconShape}
                  iconColor={checkIconColor}
                  type={checkIconType}
                  kind={checkIconKind}
                  isSidePanelHistory
                  showTail={index !== data.sidePanel.history.audit.length - 1}
                  dataTestId='CertsSidePanelBodyHistory-SpbTimeLine'
                />
                <div
                  className={
                    isMobileWidth
                      ? 'CertsSidePanelBody-tabContentTimeLineBlockBody--mobileResponsive'
                      : 'CertsSidePanelBody-tabContentTimeLineBlockBody'
                  }>
                  <Typography
                    kind='body1-bold'
                    className='CertsSidePanelBody-tabContentTimeLineBlockBodyTitle'>
                    {item?.labelKey
                      ? getTranslatedMessageUsingId(
                          item?.labelKey,
                          item?.actionMessage,
                          { 0: item?.actionMessageDynamicValue }
                        )
                      : item?.actionMessage ||
                        item.action ||
                        auditHistoryActionType?.default?.heading}
                  </Typography>
                  <FieldSet
                    key={`${item.index}-1`}
                    type='compact'
                    fieldData={{
                      label:
                        messages.CERTS_ACTIONED_ON ||
                        localize(intl, getMessageKey('CERTS_ACTIONED_ON')),
                      // TODO: Might need post Beta release
                      // item.action === 'cancelled'
                      //   ? 'Resetted On'
                      //   : auditHistoryActionType[item.action]?.date ||
                      //     auditHistoryActionType?.default?.date,
                      value: item.actionDate ? actionDate : '',
                    }}
                  />
                  <FieldSet
                    key={`${item.index}-2`}
                    type='compact'
                    fieldData={{
                      label:
                        messages.CERTS_ACTIONED_BY ||
                        localize(intl, getMessageKey('CERTS_ACTIONED_BY')),
                      // TODO: Might need post Beta release
                      // item.action === 'cancelled'
                      //   ? 'Resetted By'
                      //   : auditHistoryActionType[item.action]?.user ||
                      //     auditHistoryActionType?.default?.user,
                      value: item.actionUser,
                    }}
                  />
                  <FieldSet
                    key={`${item.index}-3`}
                    type='compact'
                    fieldData={{
                      label:
                        auditHistoryActionType[item.action]?.other ||
                        auditHistoryActionType?.default?.other,
                      value:
                        item?.comment && item?.comment?.trim()?.length !== 0
                          ? item?.comment
                          : '-',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

CertsSidePanelBodyHistory.propTypes = {
  data: PropTypes.shape(DATA).isRequired,
  isLoadingHistory: PropTypes.bool.isRequired,
  isMobileWidth: PropTypes.bool,
};

CertsSidePanelBodyHistory.defaultProps = { isMobileWidth: false };

export default CertsSidePanelBodyHistory;
